import { useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { BEFORE_AFTER } from "../data/site";
import { Reveal } from "./Reveal";
import { useGallery } from "../lib/useGallery";

const Panel = ({ src, label }) =>
  src ? (
    <img src={src} alt={label} className="keep-color absolute inset-0 h-full w-full object-cover" />
  ) : (
    <div className={`absolute inset-0 flex flex-col items-center justify-center ${label === "Before" ? "bg-neutral-300" : "bg-neutral-100"}`}>
      <img src="/figure-black.png" alt="" aria-hidden="true" className="w-1/3 opacity-15" />
      <span className="mt-4 text-[10px] uppercase tracking-[0.3em] text-neutral-500">Client photo coming soon</span>
    </div>
  );

const Compare = ({ item, i }) => {
  const [pos, setPos] = useState(50);
  return (
    <figure data-testid={`before-after-${i}`}>
      <div className="relative aspect-[4/5] select-none overflow-hidden">
        <Panel src={item.after} label="After" />
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <Panel src={item.before} label="Before" />
        </div>
        <div className="pointer-events-none absolute inset-y-0 w-px bg-white" style={{ left: `${pos}%` }}>
          <span className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white p-2 text-neutral-900 shadow"><MoveHorizontal size={14} strokeWidth={1.5} /></span>
        </div>
        <span className="absolute left-4 top-4 bg-white/85 px-2 py-1 text-[10px] uppercase tracking-[0.3em] text-neutral-900">Before</span>
        <span className="absolute right-4 top-4 bg-white/85 px-2 py-1 text-[10px] uppercase tracking-[0.3em] text-neutral-900">After</span>
        <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(+e.target.value)} aria-label={`Compare before and after ${item.treatment}`} data-testid={`before-after-slider-${i}`} className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0" />
      </div>
      <figcaption className="mt-5">
        <p className="kicker">{item.sessions}</p>
        <p className="font-display mt-1 text-2xl text-neutral-950">{item.treatment}</p>
        {item.credit && <p className="mt-2 text-xs font-light text-neutral-500" data-testid={`before-after-credit-${i}`}>{item.credit}. Individual results vary.</p>}
      </figcaption>
    </figure>
  );
};

export const BeforeAfter = () => {
  const { before_after } = useGallery();
  const items = before_after.length ? [...before_after, ...BEFORE_AFTER.filter((b) => b.before)].slice(0, 3) : BEFORE_AFTER;
  return (
  <section id="results" className="bg-white py-24 md:py-36" data-testid="before-after-section">
    <div className="mx-auto max-w-[1400px] px-4 md:px-8">
      <Reveal className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <p className="kicker">Real Results</p>
          <h2 className="font-display mt-3 text-4xl font-light text-neutral-950 md:text-5xl">Before <em>&amp;</em> After</h2>
        </div>
        <p className="max-w-sm text-sm font-light leading-relaxed text-neutral-600">Drag to compare. Manufacturer results and illustrations are shown until our own client photos are added; individual results vary.</p>
      </Reveal>
      <div className="grid gap-12 md:grid-cols-3">
        {items.map((item, i) => <Reveal key={item.id || item.treatment}><Compare item={item} i={i} /></Reveal>)}
      </div>
    </div>
  </section>
  );
};
